import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Layout,
  Result,
  Space,
  Tabs,
  Typography,
  message,
  theme,
} from 'antd';
import {
  ArrowLeftOutlined,
  MailOutlined,
} from '@ant-design/icons';
import {
  ArrowLeft,
  ChevronLeft,
} from 'lucide-react';
import { logoOnDark } from '../index';
import ip1Image from '../assets/images/ip-1.png';
import ip1FlippedImage from '../assets/images/ip-1-flipped.png';

const { Text, Title } = Typography;

export interface BaseAuthPageProps {
  /** 默认跳转目标 */
  defaultRedirect?: string;
  /** 当前是否已登录 */
  isLoggedIn: boolean;
  /** 重新发送验证邮件 */
  resendEmail: (params: { email: string }) => Promise<void>;
  /** 获取 URL 参数 */
  searchParams: URLSearchParams;
  /** 设置 URL 参数 */
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void;
  /** 导航方法 */
  navigate: (path: string, options?: { replace?: boolean }) => void;
  /** 自定义滚动条组件 */
  ScrollbarComponent?: React.ComponentType<{ style?: React.CSSProperties; children: React.ReactNode }>;
  
  /** 页面文案配置 */
  texts: {
    title: string;
    subtitle: string;
    heroTitle: string;
    heroDescription: string;
    heroPoints: string[];
  };

  /** 渲染邮件登录/注册表单 */
  renderEmailForm: (context: { 
    pendingEmail: string | null;
    setPendingEmail: (email: string | null) => void;
    updateMethodInUrl: (method: 'email' | 'phone', extras?: Record<string, string | null | undefined>) => void;
  }) => React.ReactNode;
}

type AuthMethod = 'email' | 'phone';

/**
 * 认证页面基础组件
 * 封装了 Login 和 Register 页面的公共逻辑：
 * 1. URL 参数同步 (method, pendingEmail, redirect)
 * 2. 邮箱验证提示与重发逻辑
 * 3. 手机号/邮箱 Tab 切换逻辑
 * 4. 登录状态自动跳转
 * 5. 左右分栏布局 (Logo + 营销信息 | 表单区)
 */
export const BaseAuthPage: React.FC<BaseAuthPageProps> = ({
  defaultRedirect = '/',
  isLoggedIn,
  resendEmail,
  searchParams,
  setSearchParams,
  navigate,
  ScrollbarComponent,
  texts,
  renderEmailForm,
}) => {
  const { token } = theme.useToken();
  const [activeMethod, setActiveMethod] = useState<AuthMethod>(
    searchParams.get('method') === 'phone' ? 'phone' : 'email',
  );
  const [pendingEmail, setPendingEmail] = useState<string | null>(
    searchParams.get('pendingEmail'),
  );
  const [resendingEmail, setResendingEmail] = useState(false);

  const redirectTarget = searchParams.get('redirect') || defaultRedirect;

  // 同步 URL 参数到状态
  useEffect(() => {
    setActiveMethod(searchParams.get('method') === 'phone' ? 'phone' : 'email');
    const pending = searchParams.get('pendingEmail');
    // 同步 URL 参数到状态，包括删除的情况
    setPendingEmail(pending);
  }, [searchParams]);

  // 已登录自动跳转
  useEffect(() => {
    if (isLoggedIn) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isLoggedIn, navigate, redirectTarget]);

  const updateMethodInUrl = (method: AuthMethod, extras?: Record<string, string | null | undefined>) => {
    const params = new URLSearchParams(searchParams);
    params.set('method', method);
    if (redirectTarget) {
      params.set('redirect', redirectTarget);
    }
    if (extras) {
      Object.entries(extras).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    setSearchParams(params, { replace: true });
  };

  const handleTabChange = (nextKey: string) => {
    const nextMethod: AuthMethod = nextKey === 'phone' ? 'phone' : 'email';
    setActiveMethod(nextMethod);
    if (nextMethod !== 'email') {
      setPendingEmail(null);
      updateMethodInUrl(nextMethod, { pendingEmail: null });
    } else {
      updateMethodInUrl(nextMethod);
    }
  };

  const handleBack = () => {
    const target = redirectTarget && !redirectTarget.startsWith('/auth') ? redirectTarget : defaultRedirect;
    navigate(target, { replace: false });
  };

  const handleResendEmail = async () => {
    if (!pendingEmail) return;

    setResendingEmail(true);
    try {
      await resendEmail({ email: pendingEmail });
      message.success('新的验证邮件已发送，请使用最新邮件中的链接进行验证（之前的链接已失效）', 6);
    } catch (error: any) {
      message.error(error?.message || '发送失败');
    } finally {
      setResendingEmail(false);
    }
  };

  // 暴露给子组件的上下文
  const context = {
    pendingEmail,
    setPendingEmail: (email: string | null) => {
      setPendingEmail(email);
      if (email) {
        updateMethodInUrl('email', { pendingEmail: email });
      }
    },
    updateMethodInUrl,
  };

  const buildLoginPath = () => {
    const params = new URLSearchParams();
    if (redirectTarget) {
      params.set('redirect', redirectTarget);
    }
    // 只传递 prefillEmail，不传递 pendingEmail，让用户可以正常登录
    if (pendingEmail) {
      params.set('prefillEmail', pendingEmail);
    }
    return params.toString() ? `/auth/login?${params.toString()}` : '/auth/login';
  };

  const handleGoToLogin = () => {
    console.log('[BaseAuthPage] handleGoToLogin clicked');
    console.log('[BaseAuthPage] Current title:', texts.title);
    console.log('[BaseAuthPage] Current searchParams:', Object.fromEntries(searchParams.entries()));
    console.log('[BaseAuthPage] pendingEmail:', pendingEmail);
    
    // 判断当前是否已经在登录页面
    const isLoginPage = texts.title.includes('登录') || texts.title.includes('欢迎回来');
    console.log('[BaseAuthPage] isLoginPage:', isLoginPage);
    
    if (isLoginPage) {
      // 如果已经在登录页面，清除 pendingEmail 状态，让用户重新看到登录表单
      console.log('[BaseAuthPage] In login page, clearing pendingEmail');
      const params = new URLSearchParams(searchParams);
      params.delete('pendingEmail');
      if (pendingEmail) {
        params.set('prefillEmail', pendingEmail);
      }
      console.log('[BaseAuthPage] New params:', Object.fromEntries(params.entries()));
      setSearchParams(params, { replace: true });
    } else {
      // 如果在注册页面，跳转到登录页面
      console.log('[BaseAuthPage] In register page, navigating to login');
      const loginPath = buildLoginPath();
      console.log('[BaseAuthPage] Login path:', loginPath);
      navigate(loginPath, { replace: false });
    }
  };

  // 渲染左右分栏布局
  const renderLayout = (formContent: React.ReactNode) => {
    const content = (
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          alignItems: 'stretch',
          background: token.colorBgLayout,
          position: 'relative',
        }}
      >
        {/* 左侧营销区 */}
        <div
          style={{
            flex: '1 1 50%',
            padding: '48px 56px',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #1f1f1f 0%, #111 100%)',
            color: '#fafafa',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Space direction="vertical" size={24} style={{ position: 'relative', zIndex: 10, alignItems: 'center', width: '100%', gap: 32, marginTop: '33%' }}>
            <Space direction="vertical" size={8} style={{ textAlign: 'center', gap: 12}}>
              <Title level={1} style={{ color: '#fafafa', marginBottom: 0 }}>
                {texts.heroTitle}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.72)', fontSize: 18 }}>
                让你的关注和收藏井井有条
              </Text>
            </Space>
            <Space direction="vertical" size={16} style={{ gap: 12, alignItems: 'center' }}>
              {texts.heroPoints.map((point, index) => {
                // 为每个 check mark 定义不同的渐变色
                const gradients = [
                  { start: '#F58236', end: '#F58236' }, // 橙色
                  { start: '#FFBA52', end: '#FFBA52' }, // 红色
                  { start: '#FFD95C', end: '#FFD95C' }, // 黄色
                ];
                const gradient = gradients[index % gradients.length];

                return (
                  <Space key={point} size={12} style={{ gap: 8 }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      style={{ flexShrink: 0 }}
                    >
                      <defs>
                        <linearGradient id={`checkGradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" style={{ stopColor: gradient.start, stopOpacity: 1 }} />
                          <stop offset="100%" style={{ stopColor: gradient.end, stopOpacity: 1 }} />
                        </linearGradient>
                      </defs>
                      <path
                        fill={`url(#checkGradient-${index})`}
                        fillRule="evenodd"
                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}>{point}</Text>
                  </Space>
                );
              })}
            </Space>
          </Space>

          {/* IP-1 图片 - 左下角 */}
          {/* <img
            src={ip1Image}
            alt="decoration"
            style={{
              position: 'absolute',
              bottom: 0,
              left: -60,
              width: 320,
              height: 320,
              objectFit: 'contain',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          /> */}

          {/* Blob 背景效果 */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '60%',
            pointerEvents: 'none',
            zIndex: 0,
          }}>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              filter: 'blur(100px)',
              opacity: 0.85,
            }}>
              {/* Blob 1 */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-20%',
                  left: '-10%',
                  width: '60%',
                  height: '80%',
                  background: '#ff9447',
                  borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
                  animation: 'blob1 20s infinite alternate cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 0.7,
                }}
              />
              {/* Blob 2 */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-20%',
                  right: '-10%',
                  width: '60%',
                  height: '80%',
                  background: '#FF4D4F',
                  borderRadius: '60% 40% 30% 70% / 60% 40% 70% 40%',
                  animation: 'blob2 25s infinite alternate cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 0.6,
                }}
              />
              {/* Blob 3 */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-30%',
                  left: '20%',
                  width: '50%',
                  height: '70%',
                  background: '#ffb04f',
                  borderRadius: '50% 50% 40% 60% / 50% 40% 60% 50%',
                  animation: 'blob3 22s infinite alternate cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 0.6,
                }}
              />
              {/* Blob 4 */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-10%',
                  left: '40%',
                  width: '40%',
                  height: '60%',
                  background: '#ff744a',
                  borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                  animation: 'blob4 18s infinite alternate cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 0.8,
                }}
              />
            </div>
          </div>
        </div>

        {/* 右侧表单区 */}
        <div
          style={{
            flex: '1 1 50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '48px 24px',
            background: token.colorBgLayout,
          }}
        >
          <Card
            style={{
              width: 420,
              maxWidth: '100%',
              border: 'transparent',
              background: 'transparent',
            }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Button
                  type="text"
                  icon={<ChevronLeft size={20}/>}
                  onClick={handleBack}
                  style={{ padding: '0px 6px 0px 0px', color: token.colorText, gap: 2}}
                >
                  返回
                </Button>
                <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>
                  {texts.title}
                </Title>
                <Text type="secondary">{texts.subtitle}</Text>
              </div>
              {formContent}
            </Space>
          </Card>
        </div>

        {/* 松鼠装饰图 - 父容器级别，可延伸到右侧 */}
        <img
          src={ip1FlippedImage}
          alt="decoration"
          style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-240px)',
            width: 300,
            height: 300,
            objectFit: 'contain',
            zIndex: 15,
            pointerEvents: 'none',
          }}
        />

        {/* Logo Header - 独立层，始终在最上方 */}
        <img
          src={logoOnDark}
          alt="cangcang.app"
          width={116}
          height={32}
          style={{
            position: 'absolute',
            top: 24,
            left: 24,
            cursor: 'pointer',
            zIndex: 20,
          }}
          onClick={() => navigate(defaultRedirect)}
        />

        {/* 版权信息 - 独立层，左下角 */}
        <Text
          style={{
            position: 'absolute',
            bottom: 24,
            left: 24,
            color: 'rgba(255,255,255,0.45)',
            fontSize: 12,
            zIndex: 20,
          }}
        >
          Cangcang.app © {new Date().getFullYear()}
        </Text>
      </div>
    );

    return (
      <Layout style={{ minHeight: '100vh', height: '100vh', overflow: 'hidden' }}>
        <Layout.Content style={{ padding: 0, height: '100%' }}>
          {ScrollbarComponent ? (
            <ScrollbarComponent style={{ height: '100%' }}>
              {content}
            </ScrollbarComponent>
          ) : (
            <div style={{ height: '100%', overflow: 'auto' }}>
              {content}
            </div>
          )}
        </Layout.Content>
      </Layout>
    );
  };

  // 渲染邮箱验证等待页面
  const renderPendingEmailPage = () => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '400px',
    }}>
      <Result
        icon={
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <MailOutlined style={{ fontSize: 72, color: 'var(--ant-color-primary)' }} />
          </div>
        }
        title="请验证您的邮箱"
        subTitle={
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            我们已向 <span style={{ color: 'var(--ant-color-text)' }}>{pendingEmail}</span> 发送了验证邮件
            <br />
            请在30秒~1分钟后，查看邮箱的收件箱或垃圾邮件
            <br />
            点击邮件中的链接完成验证后，即可登录
          </div>
        }
        extra={[
          <Button
            key="resend"
            type="primary"
            size="large"
            onClick={handleResendEmail}
            loading={resendingEmail}
            style={{ minWidth: 140, marginInlineEnd: 12 }}
          >
            重新发送邮件
          </Button>,
          <Button
            key="login"
            size="large"
            onClick={handleGoToLogin}
            style={{ minWidth: 140 }}
          >
            已验证，去登录
          </Button>,
        ]}
      />
    </div>
  );

  // 渲染表单页面
  const renderFormPage = () => (
    <Tabs
      activeKey={activeMethod}
      onChange={handleTabChange}
      items={[
        {
          key: 'email',
          label: '邮箱' + (texts.title.includes('注册') ? '注册' : '登录'),
          children: renderEmailForm(context),
        },
        {
          key: 'phone',
          label: '手机号' + (texts.title.includes('注册') ? '注册' : '登录'),
          children: (
            <Space
              direction="vertical"
              size="middle"
              style={{ width: '100%', textAlign: 'center' }}
            >
              <Text type="secondary">手机号功能即将上线，敬请期待。</Text>
            </Space>
          ),
        },
      ]}
    />
  );

  return renderLayout(pendingEmail ? renderPendingEmailPage() : renderFormPage());
};

// Blob 动画样式
const blobAnimationStyles = `
  @keyframes blob1 {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
    33% { transform: translate(30%, -10%) rotate(20deg) scale(1.1); border-radius: 70% 30% 50% 50% / 30% 30% 70% 70%; }
    66% { transform: translate(-20%, 20%) rotate(-10deg) scale(0.9); border-radius: 100% 60% 60% 100% / 100% 100% 60% 60%; }
    100% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
  }
  @keyframes blob2 {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 60% 40% 30% 70% / 60% 40% 70% 40%; }
    33% { transform: translate(-30%, -10%) rotate(-10deg) scale(1.2); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
    66% { transform: translate(20%, 25%) rotate(10deg) scale(0.8); border-radius: 70% 30% 50% 50% / 30% 30% 70% 70%; }
    100% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 60% 40% 30% 70% / 60% 40% 70% 40%; }
  }
  @keyframes blob3 {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 50% 50% 40% 60% / 50% 40% 60% 50%; }
    50% { transform: translate(40%, -20%) rotate(15deg) scale(1.1); border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
    100% { transform: translate(-20%, 10%) rotate(-5deg) scale(0.9); border-radius: 50% 50% 40% 60% / 50% 40% 60% 50%; }
  }
  @keyframes blob4 {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
    50% { transform: translate(-40%, -20%) rotate(-20deg) scale(1.2); border-radius: 60% 40% 30% 70% / 60% 40% 70% 40%; }
    100% { transform: translate(20%, 20%) rotate(10deg) scale(0.9); border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
  }
`;

// 将动画样式注入到 document head
if (typeof document !== 'undefined') {
  const styleId = 'blob-animations-auth-page';
  if (!document.getElementById(styleId)) {
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = blobAnimationStyles;
    document.head.appendChild(styleElement);
  }
}
